import { X } from "lucide-react";
import { DoctorSessionTimingSelection, DAYS_ORDER } from "./TimeSlots";
import ClinicForm from "./AddNewSelfClinic";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { DoctorService } from "../../../services/api/doctor.service";
import LocationPicker, { AddressDetails } from "../../GoogleMap";

interface AddNewPreferenceProps {
  type: string;
  doctorId: string;
  onClose: () => void;
  onSuccess: () => void;
}
interface TimeFrame {
  id: string;
  day_of_week: string;
  start_time: string;
  end_time: string;
  slot_duration: number;
}

interface NewTimeSlot {
  start_time: string;
  end_time: string;
  slot_duration: number;
}

export const AddNewPreference = ({
  type,
  doctorId,
  onClose,
  onSuccess,
}: AddNewPreferenceProps) => {
  const [clinicDetails, setClinicDetails] = useState({
    id: "",
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    lat: 0,
    long: 0,
    mobile_number: "",
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [consultationFee, setConsultationFee] = useState<number>(0);
  const [enabledDays, setEnabledDays] = useState<Record<string, boolean>>({});
  const [timeFrames, setTimeFrames] = useState<TimeFrame[]>([]);
  const [addingSlots, setAddingSlots] = useState<
    Record<string, NewTimeSlot | null>
  >({});
  const [clinicListing, setClinicListing] = useState([]);
  const [address, setAddress] = useState("");
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [showMap, setShowMap] = useState(false);
  const [isMonthly, setIsMonthly] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<number>(1);

  //   const parseTimeInput = (timeStr: string): string => {
  //     const [hours, minutes] = timeStr.split(":");
  //     const hour = parseInt(hours, 10);
  //     return `${hour}:${minutes || "00"}`;
  //   };

  const toggleDay = (day: string) => {
    setEnabledDays((prev) => ({ ...prev, [day]: !prev[day] }));

    if (enabledDays[day]) {
      // remove slots if disabling the day
      setTimeFrames((prev) => prev.filter((slot) => slot.day_of_week !== day));
    }
  };

  const handleAddSlotClick = (day: string) => {
    setAddingSlots((prev) => ({
      ...prev,
      [day]: { start_time: "09:00", end_time: "17:00", slot_duration: 15 },
    }));
  };

  const handleTimeSlotChange = (
    slotId: string,
    field: "start_time" | "end_time" | "slot_duration",
    value: string | number,
  ) => {
    setTimeFrames((prev) =>
      prev.map((slot) =>
        slot.id === slotId
          ? {
              ...slot,
              [field]:
                field === "slot_duration" ? Number(value) : String(value),
            }
          : slot,
      ),
    );
  };

  const handleSaveSlot = (day: string) => {
    const slot = addingSlots[day];
    if (!slot) return;

    setTimeFrames((prev) => [
      ...prev,
      {
        id: `L-${Date.now()}`,
        day_of_week: day,
        start_time: slot.start_time,
        end_time: slot.end_time,
        slot_duration: slot.slot_duration,
      },
    ]);

    setAddingSlots((prev) => ({ ...prev, [day]: null }));
  };

  const handleRemoveSlot = (slotId: string) => {
    setTimeFrames((prev) => prev.filter((s) => s.id !== slotId));
  };

  const handleClinicDetailsOnChange = (fieldName: string, value: string) => {
    setClinicDetails((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  useEffect(() => {
    setAddress(
      clinicDetails.address +
        " " +
        clinicDetails.city +
        " " +
        clinicDetails.state +
        "  " +
        clinicDetails.pincode,
    );
  }, [
    clinicDetails.address,
    clinicDetails.city,
    clinicDetails.state,
    clinicDetails.pincode,
  ]);

  const handleLocate = async () => {
    setShowMap(true);
  };

  const loadClinicListing = async () => {
    try {
      const clinicRes = await DoctorService.fetchClinicListing(1, 1000, "");

      if (clinicRes.success) {
        setClinicListing(clinicRes.data.dataList || []);
      } else {
        setClinicListing([]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSaveChangeClick = async () => {
    try {
      if (type === "Self Clinic") {
        if (
          !clinicDetails.name ||
          !clinicDetails.address ||
          !clinicDetails.city ||
          !clinicDetails.state ||
          !clinicDetails.pincode
        ) {
          toast.error("All clinic details are required for Self Clinic.");
          return;
        }
      } else if (type === "Dandar Clinic") {
        if (!clinicDetails.id || !clinicDetails.name) {
          toast.error("Clinic selection is required for Dandar Clinic.");
          return;
        }
      }

      if (type === "Self Clinic" || type === "Dandar Clinic") {
        const pinRegex = /^[1-9][0-9]{5}$/;
        if (!pinRegex.test(clinicDetails.pincode)) {
          toast.error("Please enter a valid 6-digit pincode.");
          return;
        }
      }
      const isAnyDayEnabled = Object.values(enabledDays).some((day) => day);

      if (!isAnyDayEnabled) {
        toast.error("Please enable at least one day before saving.");
        return;
      }

      if (enabledDays) {
      }
      for (const day of Object.keys(enabledDays)) {
        if (enabledDays[day]) {
          const daySlots = timeFrames.filter((s) => s.day_of_week === day);
          if (daySlots.length === 0) {
            toast.error(`Please add slots for ${day}.`);
            return;
          }
        }
      }

      for (const slot of timeFrames) {
        if (slot.start_time >= slot.end_time) {
          toast.error(
            `Start time must be less than end time for ${slot.day_of_week}.`,
          );
          return;
        }
      }
      const groupedByDay: Record<string, TimeFrame[]> = {};
      timeFrames.forEach((slot) => {
        if (!groupedByDay[slot.day_of_week])
          groupedByDay[slot.day_of_week] = [];
        groupedByDay[slot.day_of_week].push(slot);
      });

      for (const day in groupedByDay) {
        const slots = groupedByDay[day];
        for (let i = 0; i < slots.length; i++) {
          for (let j = i + 1; j < slots.length; j++) {
            const s1 = slots[i];
            const s2 = slots[j];
            if (
              s1.start_time < s2.end_time &&
              s1.end_time > s2.start_time // overlap condition
            ) {
              toast.error(
                `Overlapping slots detected on ${day}. Please adjust timings.`,
              );
              return;
            }
          }
        }
      }

      if (!consultationFee || consultationFee <= 0) {
        toast.error("Consultation fee is required.");
        return;
      }

      setIsGenerating(true);
      const payload = {
        doctor_id: doctorId,
        clinic_id: type === "Dandar Clinic" ? clinicDetails.id : "",
        is_online:
          type === "Dandar Clinic" ||
          type === "Self Clinic" ||
          type === "Instent Appointment"
            ? false
            : true,
        is_self_clinic: type === "Self Clinic",
        is_instant: type === "Instent Appointment",
        is_monthly: isMonthly,
        week_of_month: selectedWeek,
        name:
          type === "Dandar Clinic" || type === "Self Clinic"
            ? clinicDetails.name
            : "",
        address:
          type === "Dandar Clinic" || type === "Self Clinic"
            ? clinicDetails.address
            : "",
        city:
          type === "Dandar Clinic" || type === "Self Clinic"
            ? clinicDetails.city
            : "",
        state:
          type === "Dandar Clinic" || type === "Self Clinic"
            ? clinicDetails.state
            : "",
        pincode:
          type === "Dandar Clinic" || type === "Self Clinic"
            ? clinicDetails.pincode
            : "",
        mobile_number:
          type === "Dandar Clinic" || type === "Self Clinic"
            ? clinicDetails.mobile_number || ""
            : undefined,
        consultation_fee: Number(consultationFee),
        ...enabledDays,
        TimeFrame: timeFrames,
      };
      const newPreferenceRes = await DoctorService.addNewSlots(payload);
      if (newPreferenceRes.success) {
        toast.success(newPreferenceRes.message);
        onClose();
        onSuccess();
      } else {
        toast.error("Error while creating new preference");
      }
    } catch (error: any) {
      // console.log(error);
      toast.error("Something went wrong.", error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (type === "Dandar Clinic") {
      loadClinicListing();
    }
  }, []);

  const handleToggleScheduling = () => {
    setIsMonthly(!isMonthly);
  };

  const handleApplySameAs = () => {
    // Find the first day with slots based on DAYS_ORDER
    let sourceDay = "";
    let sourceSlots: TimeFrame[] = [];

    for (const day of DAYS_ORDER) {
      const slots = timeFrames.filter((s) => s.day_of_week === day);
      if (slots.length > 0) {
        sourceDay = day;
        sourceSlots = slots;
        break;
      }
    }

    if (!sourceDay) {
      toast.error("Please add slots to at least one day first.");
      return;
    }

    // Create new slots for all other days (enabling them if disabled)
    // We keep existing slots of the source day and overwrite others
    const framesToKeep = timeFrames.filter((s) => s.day_of_week === sourceDay);
    const newTimeFrames = [...framesToKeep];
    const newEnabledDays = { ...enabledDays };

    for (const day of DAYS_ORDER) {
      if (day === sourceDay) continue;

      // Enable the day
      newEnabledDays[day] = true;

      // Copy slots
      sourceSlots.forEach((slot) => {
        newTimeFrames.push({
          ...slot,
          id: `L-${Date.now()}-${day}-${Math.random()}`, // Unique ID
          day_of_week: day,
        });
      });
    }

    setEnabledDays(newEnabledDays);
    setTimeFrames(newTimeFrames);
    toast.success(`Copied slots from ${sourceDay} to all days.`);
  };

  const handleLocationSelect = (
    lat: string,
    lng: string,
    addressDetails: AddressDetails,
  ) => {
    setClinicDetails((prev) => ({
      ...prev,
      lat: parseFloat(lat),
      long: parseFloat(lng),
      address: addressDetails.fullAddress,
      city: addressDetails.city,
      state: addressDetails.state,
      pincode: addressDetails.pincode,
    }));
  };
  return (
    <section className="fixed top-0 w-[100vw] left-0 h-[100dvh] bg-[#00000071] z-50 overflow-hidden backdrop-blur-sm p-4 flex items-center justify-center">
      <div className="bg-white w-[60%] h-[80vh] max-h-[700px] rounded-md flex flex-col relative shadow-xl">
        {/* Heading */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <h1 className="text-[#199C4D] font-semibold text-lg">
            Add {type} Doctor Schedule
          </h1>
          <button
            disabled={isGenerating}
            onClick={onClose}
            className="flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X width={20} height={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
          {/* Scheduling Type */}
          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg mb-4 border border-gray-200">
            <div>
              <h4 className="font-medium text-gray-800 text-sm">
                Scheduling Type
              </h4>
              <p className="text-xs text-gray-500">
                Current:{" "}
                <span className="font-semibold text-emerald-600">
                  {isMonthly ? "Monthly" : "Weekly"}
                </span>
              </p>
            </div>

            {handleToggleScheduling && (
              <button
                onClick={handleToggleScheduling}
                type="button"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  isMonthly ? "bg-emerald-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isMonthly ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>

          {/* Monthly Week Selection */}
          {isMonthly &&
            (type === "Dandar Clinic" || type === "Self Clinic") && (
              <div className="mb-4 animate-fadeIn">
                <label className="text-sm font-medium text-[#3F5260] mb-2 block">
                  Select Week of Month
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {[1, 2, 3, 4, 5].map((week) => (
                    <button
                      key={week}
                      type="button"
                      onClick={() => setSelectedWeek(week)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-all ${
                        selectedWeek === week
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      Week {week}
                    </button>
                  ))}
                </div>
              </div>
            )}

          {/* Form Content */}
          <div className="space-y-4">
            {type === "Self Clinic" && (
              <div className="space-y-2">
                <ClinicForm
                  formData={clinicDetails}
                  onChange={(fieldName, value) =>
                    handleClinicDetailsOnChange(fieldName, value)
                  }
                  isGenerating={isGenerating}
                />
                <button
                  type="button"
                  onClick={handleLocate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <span>🗺️</span> Locate on Map
                </button>
              </div>
            )}

            {type === "Dandar Clinic" && (
              <div className="flex flex-col gap-2 items-start w-full">
                <label
                  htmlFor="clinicType"
                  className="text-sm font-medium text-[#3F5260]"
                >
                  Select Dandar Clinic
                </label>

                {/* Dropdown */}
                <select
                  disabled={isGenerating}
                  id="clinicType"
                  name="clinicType"
                  value={clinicDetails.id}
                  onChange={(e) => {
                    const selected: any = clinicListing.find(
                      (c: any) => c.id === e.target.value,
                    );
                    if (selected) {
                      setClinicDetails({
                        id: selected.id,
                        name: selected.name,
                        address: selected.address,
                        city: selected.city,
                        state: selected.state,
                        pincode: selected.pincode,
                        lat: selected.lat,
                        long: selected.long,
                        mobile_number:
                          selected.mobileNumber || selected.mobile_number || "",
                      });
                    }
                  }}
                  className="border border-[#D0D5DE] w-full text-black font-light text-sm p-2 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">-- Select a Clinic --</option>
                  {clinicListing.length > 0 ? (
                    clinicListing.map((clinic: any) => (
                      <option key={clinic.id} value={clinic.id}>
                        {clinic.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No clinics found</option>
                  )}
                </select>

                {clinicDetails.id && (
                  <div className="w-full mt-2">
                    <label className="block text-sm font-medium text-[#3F5260] mb-1">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      disabled={isGenerating}
                      maxLength={10}
                      placeholder="Enter clinic mobile number"
                      value={clinicDetails.mobile_number || ""}
                      onChange={(e) =>
                        handleClinicDetailsOnChange("mobile_number", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-[#D0D5DE] rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                )}
              </div>
            )}

            <DoctorSessionTimingSelection
              consultationFee={consultationFee}
              enabledDays={enabledDays}
              timeFrames={timeFrames}
              addingSlots={addingSlots}
              setConsultationFee={setConsultationFee}
              setAddingSlots={setAddingSlots}
              toggleDay={toggleDay}
              handleAddSlotClick={handleAddSlotClick}
              handleSaveSlot={handleSaveSlot}
              handleRemoveSlot={handleRemoveSlot}
              handleTimeSlotChange={handleTimeSlotChange}
              handleApplySameAs={handleApplySameAs}
              isGenerating={isGenerating}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 shrink-0 bg-gray-50 rounded-b-md">
          <button
            disabled={isGenerating}
            onClick={handleSaveChangeClick}
            className="w-full py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed text-sm font-medium transition-colors shadow-sm"
          >
            {isGenerating ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {showMap && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <LocationPicker
              location={location}
              setLocation={setLocation}
              address={address}
              setShowMap={setShowMap}
              onLocationSelect={handleLocationSelect}
            />
          </div>
        )}
      </div>
    </section>
  );
};
